import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Stack,
  Button,
  Container,
  Paper,
  CardContent,
  Box,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { useNavigate } from 'react-router-dom';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  updateCategoryContentSequence
} from '../../services/categories.service';
import {
    getListSequence
} from '../../services/content.service';
import ROUTE_PATH from '../../config/routes.config';
import editUrl from '../../utils/url-edit.util';

const ManageCategoryOrder = ({ id }) => {
  const navigate = useNavigate();
  const [vidOptions, setVidOptions] = useState({
    error: false,
    loading: true,
  });
  const [action, setAction] = useState({
    show: false,
    data: '',
  });
  const [categoryContentList, setCategoryContentList] = useState([]);
  const [disable, setDisable] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleUpdateCategoriesSequence = () => {
    setDisable(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const updatedContentCategorySequence = categoryContentList.map(
      (content) => `${content.content_id} - ${content.content_type}`
    );

    const payload = {
      categoryId: id,
      newSequence: updatedContentCategorySequence,
    };

    updateCategoryContentSequence(payload)
      .then((res) => {
        if (res?.data?.success) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: `Category sequence updated successfully.`,
          });
          setDisable(false);
        }
      })
      .catch(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Something went wrong.',
        });
      });
  };
  const handleOnDragEndCategories = (result) => {
    if (result.destination) {
      const newCategories = Array.from(categoryContentList);
      const [draggedItem] = newCategories.splice(result.source.index, 1);

      newCategories.splice(result.destination.index, 0, draggedItem);
      setCategoryContentList(newCategories);
    }
  };

  const handleBack = () => {
    const url = editUrl(ROUTE_PATH.CATEGORY_CONTENT, id);
    navigate(url);
  };

  useEffect(() => {
    setAction({ ...action, show: true, data: id });

    let uri = 'content-category';
    uri += `?categoryId=${id}`;

    getListSequence(uri)
      .then((res) => {
        setVidOptions({
          ...vidOptions,
          loading: false,
          error: false,
        });
        setCategoryContentList(res.data);
      })
      .catch(() =>
        setVidOptions({
          ...vidOptions,
          loading: false,
          error: true,
        })
      );
  }, [id]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 3, py: 1 }}
      >
        <Box fontWeight="bold" fontSize="20px">
          Manage Content Order
        </Box>

        <Box>
          <Button
            color="primary"
            variant="contained"
            disabled={disable}
            sx={{ marginRight: 2 }}
            onClick={() => handleUpdateCategoriesSequence()}
          >
            Save Order
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={() => handleBack()}
          >
            Back
          </Button>
        </Box>
      </Stack>

      <Container
        maxWidth="false"
        sx={{ maxHeight: '74vh', overflowY: 'auto', paddingX: 0, paddingY: 1 }}
      >
        <DragDropContext onDragEnd={handleOnDragEndCategories}>
          <Droppable droppableId="categories" isDropDisabled={false}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {!vidOptions.loading &&
                  categoryContentList.length > 0 &&
                  categoryContentList.map((category, index) => (
                    <Draggable
                      key={`category-${category.id}`}
                      draggableId={category.id.toString()}
                      index={index}
                    >
                      {(provide) => (
                        <Card
                          sx={{
                            marginBottom: '5px',
                          }}
                          ref={provide.innerRef}
                          {...provide.draggableProps}
                          {...provide.dragHandleProps}
                        >
                          <CardContent
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingY: '12px',
                              '&:last-child': {
                                paddingBottom: '12px',
                              },
                            }}
                          >
                            <div>
                              {category.title} ({category.content_type})
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </Draggable>
                  ))}

                {!vidOptions.loading && categoryContentList.length === 0 && (
                  <Paper
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '10vh',
                    }}
                  >
                    No data available.
                  </Paper>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Container>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ManageCategoryOrder.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ManageCategoryOrder;
